/**
 * PreviewModal — екран попереднього перегляду згенерованих карток.
 *
 * Сценарій: Gemini повернув JSON разом із визначеними предметом і темою →
 * показуємо їх у редагованих полях (користувач нічого не вводив, але може
 * виправити) → показуємо картки → і лише після «Зберегти» вони йдуть у D1
 * (POST /api/cards), де колода створюється за «предмет + тема», якщо її ще немає.
 *
 * Користувач може:
 *  • поправити предмет/тему, визначені моделлю;
 *  • відредагувати питання / відповідь / підказку просто в полі;
 *  • змінити теги (як визначені Gemini, так і додані вручну);
 *  • видалити погану картку, додати порожню, продублювати;
 *  • додати теги до всіх відразу, примусово привʼязати до своєї колоди;
 *  • побачити підсвічені наголоси й KaTeX у реальному вигляді.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  BrainCircuit,
  Check,
  Copy,
  Filter,
  Layers,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  TriangleAlert,
  Wand2,
} from 'lucide-react'

import { Modal, Field, TagPill, Alert, Empty, Progress } from './ui.jsx'
import RichText from './RichText.jsx'
import { parseTagInput, normalizeTag } from '../lib/tags.js'
import { NMT_SUBJECT_OPTIONS, deckTitleFor, findDeckFor } from '../lib/decks.js'

/** Картка у стані редагування + стан розкриття відповіді. */
const newDraft = (raw) => ({
  id: `draft-${Math.random().toString(36).slice(2, 10)}`,
  front: String(raw?.front ?? ''),
  back: String(raw?.back ?? ''),
  hint: String(raw?.hint ?? ''),
  tags: parseTagInput(raw?.tags),
  image_url: raw?.image_url ?? '',
  back_image_url: raw?.back_image_url ?? '',
  open: true,
  selected: true,
})

/** Без питання або відповіді картка в базу не потрапить. */
const isIncomplete = (d) => !d.front.trim() || !d.back.trim()

export default function PreviewModal({
  open,
  cards: incoming = [],
  decks = [],
  subject: detectedSubject = 'математика',
  subjectLabel: detectedLabel = null,
  subjectSource = 'model',
  topic: detectedTopic = null,
  topicTag: detectedTopicTag = null,
  totalCardsGenerated = null,
  warnings = [],
  sourceLabel = null,
  settings,
  onClose,
  onSave,
  saving = false,
  error = null,
}) {
  const [drafts, setDrafts] = useState([])
  const [bulkTags, setBulkTags] = useState('')
  const [showAll, setShowAll] = useState(true)
  const [confirmClear, setConfirmClear] = useState(false)

  // Визначене Gemini — редагується, але за замовчуванням уже заповнене
  const [subject, setSubject] = useState(detectedSubject)
  const [topic, setTopic] = useState(detectedTopic ?? '')
  const [deckId, setDeckId] = useState(null) // null = «вибрати автоматично»

  // Скидаємо стан щоразу, коли відкривається з новими картками
  useEffect(() => {
    if (open) {
      setDrafts(incoming.map(newDraft))
      setBulkTags('')
      setConfirmClear(false)
      setShowAll(true)
      setSubject(detectedSubject)
      setTopic(detectedTopic ?? '')
      setDeckId(null)
    }
  }, [open, incoming, detectedSubject, detectedTopic])

  const kept = useMemo(() => drafts.filter((d) => d.selected && !isIncomplete(d)), [drafts])
  const generatedTotal = Number.isInteger(totalCardsGenerated) ? totalCardsGenerated : incoming.length

  /** Куди потраплять картки: вказана колода, знайдена за предметом+темою або нова. */
  const targetDeck = useMemo(() => {
    if (deckId) return { id: deckId, title: decks.find((d) => String(d.id) === deckId)?.title ?? 'вибрана колода', existing: true }
    const found = findDeckFor(decks, subject, topic)
    if (found) return { ...found, existing: true }
    return { id: null, title: deckTitleFor(subject, topic), existing: false }
  }, [decks, deckId, subject, topic])

  const patch = useCallback((id, changes) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...changes } : d)))
  }, [])

  const patchTags = useCallback(
    (id, tags) => {
      setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, tags: parseTagInput(tags) } : d)))
    },
    [],
  )

  const remove = useCallback((id) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id))
  }, [])

  const toggle = useCallback((id) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, selected: !d.selected } : d)))
  }, [])

  const addBlank = useCallback(() => {
    setDrafts((prev) => [...prev, { ...newDraft({}), open: true, selected: true }])
  }, [])

  const duplicate = useCallback((id) => {
    setDrafts((prev) => {
      const i = prev.findIndex((d) => d.id === id)
      if (i === -1) return prev
      const copy = { ...prev[i], id: `draft-${Math.random().toString(36).slice(2, 10)}` }
      return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)]
    })
  }, [])

  /** Масове додавання тегів до всіх відмічених карток. */
  const applyBulkTags = useCallback(() => {
    const extra = parseTagInput(bulkTags)
    if (!extra.length) return
    setDrafts((prev) =>
      prev.map((d) =>
        d.selected ? { ...d, tags: [...new Set([...d.tags, ...extra])].slice(0, 8) } : d,
      ),
    )
    setBulkTags('')
  }, [bulkTags])

  /**
   * Колода з випадаючого списку. «Автоматично» = null, тоді сервер сам знайде
   * колоду за предметом і темою або створить нову. Ручний вибір одразу
   * додає тег-назву колоди, щоб її можна було знайти в картці.
   */
  const applyBulkDeck = useCallback(
    (value) => {
      const id = value ? Number(value) : null
      setDeckId(id)
      const deckTag = id ? normalizeTag(decks.find((d) => String(d.id) === id)?.title ?? '') : null
      if (!deckTag) return
      setDrafts((prev) =>
        prev.map((d) =>
          d.selected && !d.tags.includes(deckTag)
            ? { ...d, tags: [...d.tags, deckTag].slice(0, 8) }
            : d,
        ),
      )
    },
    [decks],
  )

  const visible = showAll ? drafts : drafts.filter((d) => d.selected || d.error)

  const handleSave = useCallback(() => {
    if (!kept.length) return
    const cleanTopic = topic.trim()
    // Якщо користувач поправив тему — старий тег теми більше не потрібен,
    // інакше фільтри показували б картки за двома темами одразу.
    const topicChanged = (detectedTopic ?? '').trim() !== cleanTopic
    onSave?.(
      kept.map(({ front, back, hint, tags, image_url, back_image_url }) => ({
        front: front.trim(),
        back: back.trim(),
        hint: hint.trim(),
        tags,
        image_url: image_url || '',
        back_image_url: back_image_url || '',
        source: 'gemini',
      })),
      {
        deckId: targetDeck.existing ? targetDeck.id : null,
        subject,
        topic: cleanTopic || null,
        sourceLabel,
        dropTags: topicChanged ? [detectedTopicTag].filter(Boolean) : [],
      },
    )
  }, [kept, onSave, targetDeck, subject, topic, detectedTopic, detectedTopicTag, sourceLabel])

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      className="review-modal"
      closeOnBackdrop={false}
      title={
        <span className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-brand-500" /> Перевірте картки перед збереженням
        </span>
      }
      subtitle={`Згенеровано ${generatedTotal} карток за матеріалом. До бази піде ${kept.length}.`}
      footer={
        <>
          <div className="mr-auto flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span>
              вибрано <b className="tabular-nums text-slate-900 dark:text-white">{kept.length}</b> з{' '}
              {drafts.length}
            </span>
            {drafts.some((d) => d.error) && (
              <span className="flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400">
                <TriangleAlert className="h-3.5 w-3.5" /> є неповні
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving}>
            Скасувати
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={saving || !kept.length}
          >
            {saving ? (
              <>
                <RotateCcw className="h-4 w-4 animate-spin" /> Зберігаю…
              </>
            ) : (
              <>
                <Check className="h-4 w-4" /> Зберегти в базу ({kept.length})
              </>
            )}
          </button>
        </>
      }
    >
      {error && (
        <Alert tone="error" className="mb-4" title="Не вдалося зберегти">
          {error}
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert tone="warn" className="mb-4" title="Перевірте визначення предмета">
          <ul className="list-disc space-y-0.5 pl-4">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* ── Визначений Gemini контекст: предмет, тема, колода ── */}
      <div className="ai-context mb-4 rounded-xl border border-brand-200 bg-brand-50/50 p-3 dark:border-brand-900 dark:bg-brand-950/20">
        <div className="mb-2.5 flex items-center gap-2">
          <BrainCircuit className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
          <p className="text-sm font-semibold">Визначено автоматично</p>
          {subjectSource === 'model' ? (
            <span className="chip !bg-brand-100 text-brand-800 dark:!bg-brand-900/60 dark:text-brand-200">
              Gemini
            </span>
          ) : (
            <span className="chip !bg-amber-100 text-amber-800 dark:!bg-amber-900/60 dark:text-amber-200">
              за замовчуванням — перевірте
            </span>
          )}
          {detectedLabel && (
            <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
              Gemini відповів: {detectedLabel}
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Предмет" hint="Змінити → зберегеться в іншу колоду">
            <select
              className="input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            >
              {NMT_SUBJECT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Тема" hint="Порожньо → колода буде створена лише за предметом">
            <input
              className="input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              maxLength={80}
              placeholder="напр.: Тригонометрія"
            />
          </Field>
        </div>

        <p className="deck-destination mt-2.5 flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-400">
          <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {targetDeck.existing ? (
            <span>
              Картки підуть у колоду <b className="text-slate-900 dark:text-white">{targetDeck.title}</b>.
            </span>
          ) : (
            <span>
              Колоди <b className="text-slate-900 dark:text-white">{targetDeck.title}</b> ще немає —
              вона створиться автоматично.
            </span>
          )}
        </p>
      </div>

      {/* Панель масових дій */}
      <div className="sticky -top-4 z-10 mb-4 rounded-xl border border-slate-200 bg-slate-50/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Додати теги всім вибраним" className="min-w-56 flex-1">
            <div className="flex gap-2">
              <input
                className="input"
                placeholder="наприклад: дати, 2019, тригонометрія"
                value={bulkTags}
                onChange={(e) => setBulkTags(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyBulkTags())}
              />
              <button type="button" onClick={applyBulkTags} className="btn-outline shrink-0">
                <Wand2 className="h-4 w-4" /> Додати
              </button>
            </div>
          </Field>

          <Field label="Колода вручну" hint="Порожньо — визначиться за предметом і темою">
            <select
              className="input"
              value={deckId ?? ''}
              onChange={(e) => applyBulkDeck(e.target.value)}
            >
              <option value="">— автоматично —</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          </Field>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="btn-ghost"
              title="Показати лише вибрані"
            >
              <Filter className="h-4 w-4" /> {showAll ? 'Усі' : 'Вибрані'}
            </button>
            <button type="button" onClick={addBlank} className="btn-outline">
              <Plus className="h-4 w-4" /> Порожня
            </button>
          </div>
        </div>

        {drafts.length > 0 && (
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDrafts((p) => p.map((d) => ({ ...d, selected: true })))}
              className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              вибрати всі
            </button>
            <button
              type="button"
              onClick={() => setDrafts((p) => p.map((d) => ({ ...d, selected: false })))}
              className="text-xs font-medium text-slate-500 hover:underline dark:text-slate-400"
            >
              зняти всі
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirmClear) {
                  setDrafts([])
                  setConfirmClear(false)
                } else {
                  setConfirmClear(true)
                  setTimeout(() => setConfirmClear(false), 3000)
                }
              }}
              className={`ml-auto text-xs font-medium transition ${
                confirmClear
                  ? 'text-rose-600 hover:underline dark:text-rose-400'
                  : 'text-slate-400 hover:text-rose-500'
              }`}
            >
              {confirmClear ? 'Точно очистити всі?' : 'очистити список'}
            </button>
          </div>
        )}
      </div>

      {!drafts.length ? (
        <Empty icon={Trash2} title="Список порожній">
          Можна додати картки вручну кнопкою «Порожня» або закрити вікно й спробувати ще раз.
        </Empty>
      ) : (
        <div className="space-y-3">
          {visible.map((draft, index) => (
            <CardDraft
              key={draft.id}
              draft={draft}
              index={index}
              total={drafts.length}
              subject={subject}
              settings={settings}
              onToggle={toggle}
              onRemove={remove}
              onDuplicate={duplicate}
              onPatch={(changes) => patch(draft.id, changes)}
              onTags={(tags) => patchTags(draft.id, tags)}
              onToggleOpen={() => patch(draft.id, { open: !draft.open })}
            />
          ))}
        </div>
      )}

      {drafts.length > 0 && (
        <div className="mt-4">
          <Progress value={kept.length} max={drafts.length} className="h-1" />
        </div>
      )}
    </Modal>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function CardDraft({ draft, index, total, subject, settings, onToggle, onRemove, onDuplicate, onPatch, onTags, onToggleOpen }) {
  const hasError = !draft.front.trim() || !draft.back.trim()
  const [tagInput, setTagInput] = useState('')

  const commitTag = () => {
    const t = normalizeTag(tagInput)
    if (t && !draft.tags.includes(t)) onTags([...draft.tags, t])
    setTagInput('')
  }

  return (
    <div
      className={`rounded-xl border transition ${
        hasError
          ? 'border-amber-300 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20'
          : draft.selected
            ? 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
            : 'border-slate-200 bg-slate-50/70 opacity-60 dark:border-slate-800 dark:bg-slate-900/50'
      }`}
    >
      {/* Шапка */}
      <div className="flex items-center gap-2 border-b border-slate-200/70 px-3 py-2 dark:border-slate-800">
        <input
          type="checkbox"
          checked={draft.selected}
          onChange={() => onToggle()}
          className="h-4 w-4 shrink-0 accent-brand-600"
          aria-label="Зберегти цю картку"
        />
        <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-400">
          {index + 1}/{total}
        </span>

        {hasError && (
          <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
            <TriangleAlert className="h-3.5 w-3.5" />
            {!draft.front.trim() ? 'немає питання' : 'немає відповіді'}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onToggleOpen}
            className="btn-ghost !px-2 !py-1 text-xs"
            title={draft.open ? 'Згорнути' : 'Розгорнути'}
          >
            {draft.open ? 'згорнути' : 'переглянути'}
          </button>
          <button
            type="button"
            onClick={() => onDuplicate()}
            className="btn-ghost !px-2 !py-1"
            title="Дублювати"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onRemove()}
            className="btn-ghost !px-2 !py-1 text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
            title="Видалити картку"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {draft.open && (
        <div className="space-y-3 p-3">
          <div className="grid gap-3 lg:grid-cols-2">
            <Field label="Питання (лицьова сторона)" required>
              <textarea
                className="input min-h-24 resize-y font-mono text-[13px]"
                value={draft.front}
                onChange={(e) => onPatch({ front: e.target.value })}
                placeholder="Напр.: Знайти похідну функції $f(x) = x^3$"
              />
            </Field>
            <Field label="Відповідь (оборотна сторона)" required>
              <textarea
                className="input min-h-24 resize-y font-mono text-[13px]"
                value={draft.back}
                onChange={(e) => onPatch({ back: e.target.value })}
                placeholder="Напр.: $f'(x) = 3x^2$"
              />
            </Field>
          </div>

          <Field label="Підказка" hint="Показується на лицьовій стороні за кнопкою «Підказка». Залиште порожнім, якщо не потрібна.">
            <input
              className="input"
              value={draft.hint}
              onChange={(e) => onPatch({ hint: e.target.value })}
              placeholder="Напр.: скористайся правилом добутку"
            />
          </Field>

          {/* Теги */}
          <Field label="Теги" hint="Без #. Клік по тегу видаляє його.">
            <div className="flex flex-wrap items-center gap-1.5">
              {draft.tags.map((t) => (
                <TagPill key={t} name={t} onRemove={() => onTags(draft.tags.filter((x) => x !== t))} />
              ))}
              <div className="flex min-w-40 flex-1 gap-1.5">
                <input
                  className="input !py-1 text-xs"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault()
                      commitTag()
                    } else if (e.key === 'Backspace' && !tagInput && draft.tags.length) {
                      onTags(draft.tags.slice(0, -1))
                    }
                  }}
                  placeholder="додати тег…"
                />
                <button type="button" onClick={commitTag} className="btn-outline !px-2 !py-1" title="Додати тег">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </Field>

          {/* Прев'ю */}
          <div>
            <p className="label">Як це побачить користувач</p>
            <div className="grid gap-2 lg:grid-cols-2">
              <div className="card-face p-3">
                <p className="mb-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">Питання</p>
                {draft.front.trim() ? (
                  <RichText tags={draft.tags} highlightStress={settings?.highlightStress !== false} className="text-sm">
                    {draft.front}
                  </RichText>
                ) : (
                  <p className="text-sm text-slate-400 italic">— порожньо —</p>
                )}
              </div>
              <div className="card-face p-3">
                <p className="mb-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">Відповідь</p>
                {draft.back.trim() ? (
                  <RichText tags={draft.tags} highlightStress={settings?.highlightStress !== false} className="text-sm">
                    {draft.back}
                  </RichText>
                ) : (
                  <p className="text-sm text-slate-400 italic">— порожньо —</p>
                )}
              </div>
            </div>
          </div>

          {subject === 'мова' && !draft.tags.includes('наголоси') && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Підказка: якщо відповідь — слово з наголосом, позначте його комбінованим знаком U+0301
              одразу після ударної букви й додайте тег «наголоси», щоб увімкнулася підсвітка й озвучка.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
